import {
  Document,
  Link,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer';
import React from 'react';

export type Note = {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  chapter: string;
  date: string;
  pageNumber: number;
};

type Props = {
  notes: Note[];
  title: string;
  author: string;
  websiteUrl?: string;
  logoText?: string;
  landingPageUrl?: string;
};

const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    fontFamily: 'Helvetica',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    // removed paddingHorizontal
  },
  header: {
    height: 50,
    marginBottom: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1 solid #e0e0e0',
    backgroundColor: 'white',
    position: 'relative',

    paddingHorizontal: 40,
  },
  logoSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
    letterSpacing: 1,
  },
  websiteUrl: {
    fontSize: 10,
    color: '#666',
    fontWeight: 'normal',
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: '#fff',
    paddingHorizontal: 40,
  },

  bookInfoSection: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottom: '1 solid #f0f0f0',
    flexShrink: 0,
  },
  bookInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bookInfoMeta: {
    flexDirection: 'column',
    flexShrink: 1,
    minWidth: 0,
  },
  bookTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#000',
    lineHeight: 1.2,
  },
  bookAuthor: {
    fontSize: 13,
    marginBottom: 15,
    color: '#666',
    fontStyle: 'italic',
  },
  viewBookButton: {
    backgroundColor: '#ff6b35',
    color: 'white',
    padding: '8 16',
    fontSize: 11,
    fontWeight: 'bold',
    borderRadius: 6,
    alignSelf: 'flex-start',
    marginBottom: 10,
    textAlign: 'center',
    textDecoration: 'none',
  },
  notesHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#000',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    flexShrink: 0,
  },
  notesContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  noteCard: {
    marginBottom: 14, // tighter, was 18
    backgroundColor: '#fafafa',
    border: '1 solid #e8e8e8',
    borderRadius: 8,
    padding: 13, // tighter than 15
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
    maxWidth: '100%',
    minWidth: 0,
    wordBreak: 'break-word',
  },

  noteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 7,
    paddingBottom: 5,
    borderBottom: '1 solid #e0e0e0',
  },
  chapterText: {
    fontSize: 11,
    color: '#666',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  pageNumberText: {
    fontSize: 10,
    color: '#888',
    backgroundColor: '#f0f0f0',
    padding: '3 8',
    borderRadius: 12,
  },
  noteTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#222',
    marginBottom: 5,
    lineHeight: 1.3,
  },
  noteDate: {
    fontSize: 9,
    color: '#999',
    marginBottom: 13,
    fontStyle: 'italic',
  },
  noteExcerpt: {
    fontSize: 11,
    fontStyle: 'italic',
    color: '#555',
    marginBottom: 8,
    backgroundColor: '#f8f8f8',
    padding: 8,
    borderLeft: '3 solid #ff6b35',
    borderRadius: 4,
    wordBreak: 'break-word',
    minWidth: 0,
  },
  noteContentBox: {
    backgroundColor: '#ffffff',
    padding: 11,
    borderRadius: 6,
    marginBottom: 8,
    border: '1 solid #ddd',
  },
  noteContent: {
    fontSize: 11,
    lineHeight: 1.5,
    color: '#333',
    fontStyle: 'normal',
    wordBreak: 'break-word',
    minWidth: 0,
  },
  footer: {
    height: 60,
    marginTop: 20,
    borderTop: '2 solid #e0e0e0',
    paddingTop: 12,
    paddingHorizontal: 40,
    paddingBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    flexShrink: 0,
  },
  footerLogo: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333',
    letterSpacing: 1,
  },
  footerUrl: {
    fontSize: 10,
    color: '#666',
    fontWeight: 'normal',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 5,
    right: 40,
    fontSize: 9,
    color: '#999',
  },
});

const A4_HEIGHT = 842;
const HEADER_HEIGHT = 50;
const HEADER_MARG = 20;
const FOOTER_MARG = 20;
const FOOTER_HEIGHT = 60;
const BOOKINFO_HEIGHT = 48;
const NOTES_HEADER_HEIGHT = 15;

function estimateNoteHeight(note: Note) {
  const textLines = (str: string | undefined, charsPerLine = 70): number => {
    if (!str) return 0;
    // count hard line breaks
    const newlineCount = (str.match(/\n/g) || []).length;
    // for each paragraph, decide how many lines printed based on card width and font
    const withoutNewlines = str.replace(/\n/g, '');
    const wrapLines = Math.ceil(withoutNewlines.length / charsPerLine);
    return wrapLines + newlineCount;
  };
  const excerptLines = textLines(note.excerpt);
  const contentLines = textLines(note.content);
  return (
    43 + // ~header, date, margin/pad
    excerptLines * 13 +
    contentLines * 14 +
    11 // for spacing fudge at bottom
  );
}

// -----------------------------------------

const NotePdfDocument = ({
  notes,
  title,
  author,
  websiteUrl = 'Zebralearn.com',
  logoText = 'ZEBRALEARN',
  landingPageUrl = '',
}: Props) => {
  const renderHeader = () => (
    <View style={styles.header} fixed>
      <View style={styles.logoSection}>
        <Svg width={119} height={10} viewBox='0 0 119 10'>
          <Path
            d='M3.47228 0.0228089C3.46258 0.0282012 2.76263 0.0324446 1.73586 0.0324446H0.015625L0.0231926 2.30499C0.027466 3.55472 0.0318283 4.59247 0.0339653 4.60962C0.03717 4.63853 0.0588044 4.6128 0.248615 4.36333C0.365066 4.21128 0.571079 3.94033 0.707028 3.76255C0.982035 3.40276 1.70255 2.44528 2.09722 1.91301C2.16302 1.82523 2.27412 1.67636 2.34419 1.58106C2.41542 1.48683 2.51895 1.34548 2.57496 1.26945C2.63104 1.19236 2.69248 1.11528 2.71081 1.09706C2.72915 1.07886 2.76904 1.03068 2.79815 0.990014C2.92324 0.817631 3.04192 0.655853 3.07103 0.616248C3.14003 0.523075 3.45822 0.0893759 3.48083 0.0571974C3.50576 0.0228089 3.50032 0.00459794 3.47228 0.0228089Z'
            fill='black'
          />
          <Path
            d='M9.99111 0.0402902C9.98574 0.0531081 9.88974 0.190219 9.7798 0.343333C9.63422 0.545767 9.46911 0.753518 9.17153 1.10906C8.94718 1.37569 8.69374 1.67987 8.60746 1.78269C8.52222 1.8855 8.41656 2.01076 8.37449 2.06115C8.33236 2.11155 8.24939 2.21002 8.19001 2.28065C8.1307 2.35138 8.00774 2.49804 7.91601 2.6073C7.69608 2.86967 7.67339 2.89646 7.19339 3.46939C6.70808 4.04984 6.77707 3.96842 6.61093 4.1644C6.53654 4.25113 6.45027 4.355 6.41797 4.3947C6.3867 4.43323 6.34029 4.48893 6.31438 4.51889C6.28956 4.5478 6.23024 4.61853 6.18389 4.67528C6.1084 4.7674 5.96921 4.93226 5.90567 5.00617C5.8722 5.04577 5.80427 5.12719 5.62416 5.34351C5.43756 5.56734 5.20349 5.84687 5.14527 5.91645C5.12265 5.94324 4.97167 6.12628 4.80874 6.32334C4.6459 6.5204 4.46794 6.7346 4.41398 6.79994C4.36003 6.86421 4.18322 7.07948 4.02243 7.27645C3.86067 7.47355 3.71074 7.65667 3.68919 7.68234C3.55218 7.8473 3.41205 8.01865 3.17692 8.30986C3.10035 8.40411 3.01515 8.50801 2.98711 8.54014C2.93316 8.6034 2.67648 8.91929 2.17498 9.54156L1.86328 9.92702L2.30977 9.93348C2.5546 9.93667 3.16614 9.94099 3.66765 9.94206C4.54877 9.94312 4.58118 9.94206 4.59516 9.92277C4.60379 9.91206 4.71267 9.78901 4.83678 9.64865C4.96187 9.50837 5.13556 9.31135 5.22397 9.21064C5.31131 9.10993 5.41814 8.99113 5.46124 8.94716C5.53352 8.87113 5.60039 8.79305 6.04903 8.25113C6.15359 8.12582 6.26364 7.99298 6.29278 7.95667C6.32295 7.92128 6.43725 7.78433 6.54627 7.65142C6.65522 7.51865 6.79318 7.35262 6.85365 7.28199C6.91298 7.21128 7.02401 7.0774 7.10063 6.98316C7.17718 6.88999 7.36911 6.65757 7.52663 6.46809C7.96236 5.94543 8.49732 5.29966 8.55767 5.22682C8.58677 5.19039 8.75822 4.98265 8.93939 4.76528C9.12063 4.54683 9.32236 4.30373 9.38705 4.22443C9.45277 4.14523 9.55739 4.01881 9.62001 3.94491C9.68256 3.871 9.79353 3.73716 9.86801 3.64823C9.94139 3.55823 10.0082 3.48548 10.0158 3.48548C10.0266 3.48548 10.0298 3.35376 10.0298 2.83432C10.0298 1.85438 10.0158 0.015625 10.0093 0.015625C10.0062 0.015625 9.99753 0.0263222 9.99111 0.0402902Z'
            fill='black'
          />
          <Path
            d='M5.58028 0.0408199L5.4487 0.0450633L5.23948 0.282773C4.70022 0.893196 4.59882 1.0067 4.53517 1.07203C4.46937 1.13842 4.36583 1.26156 4.05841 1.63426C3.98077 1.7285 3.83512 1.90415 3.73488 2.02517C3.63454 2.1462 3.47278 2.34112 3.37573 2.45887C3.27762 2.57671 3.14817 2.733 3.08665 2.80691C3.02514 2.88081 2.96913 2.94826 2.96157 2.95684C2.93779 2.98681 2.74585 3.21815 2.31877 3.73327C2.08472 4.01598 1.86152 4.28587 1.82261 4.33298C1.78486 4.38011 1.74712 4.4251 1.73954 4.43261C1.72228 4.45083 1.50762 4.70896 1.44406 4.78817C1.39661 4.84925 1.34265 4.91237 1.04067 5.27544C0.956531 5.37506 0.818448 5.54205 0.7344 5.64487C0.6492 5.74768 0.567204 5.8473 0.55109 5.86552C0.535955 5.88372 0.42387 6.01864 0.301988 6.16539C0.180107 6.31213 0.062588 6.44912 0.0409536 6.47064L0 6.5092V7.15069C0 8.48182 0.0139774 9.96622 0.0259079 9.94579C0.0324066 9.93508 0.132654 9.7948 0.248125 9.6331C0.436868 9.37069 0.580296 9.19189 1.05598 8.62749C1.10557 8.56863 1.21454 8.43905 1.29867 8.33835C1.48092 8.11998 1.97708 7.52877 2.59503 6.79092C2.96174 6.35288 3.98202 5.13629 4.49652 4.52058C4.78017 4.18217 5.56861 3.23115 5.82956 2.91308C5.87159 2.86278 6.07441 2.61428 6.28148 2.36154C6.48856 2.1088 6.70962 1.83891 6.77325 1.76288C7.01917 1.46409 8.11931 0.10827 8.14945 0.0665446C8.16027 0.0515165 8.0589 0.0493951 7.10331 0.0462123C6.52195 0.0451516 5.96978 0.0419689 5.87808 0.0397592C5.78629 0.0386985 5.65141 0.0386985 5.58028 0.0408199Z'
            fill='black'
          />
          <Path
            d='M9.84516 5.52199C9.76419 5.62905 9.62943 5.80576 9.54633 5.915C9.20012 6.36481 8.21005 7.6799 7.83585 8.18656C7.78295 8.2594 7.67302 8.406 7.59309 8.51316C7.51336 8.62018 7.42385 8.74231 7.39357 8.78302C7.36454 8.82479 7.33205 8.86011 7.3224 8.86337C7.31378 8.8655 7.27812 8.90621 7.24474 8.95224C7.2124 8.99827 7.13805 9.09898 7.08088 9.17607C7.02371 9.2521 6.95792 9.3421 6.93529 9.37422C6.91274 9.40621 6.8102 9.5455 6.70989 9.68259C6.60849 9.81961 6.52539 9.93642 6.52539 9.94281C6.52539 9.94919 7.1855 9.95139 8.27047 9.95032L10.0145 9.94713L10.0102 8.40493C10.0081 7.55678 10.0026 6.51794 9.99943 6.09491L9.99288 5.32812L9.84516 5.52199Z'
            fill='black'
          />
          <Path
            d='M0.0166016 0.0368416L1.18111 1.09501L2.71153 1.1012L3.49766 0.015625L0.0166016 0.0368416Z'
            fill='#646464'
          />
          <Path
            d='M0.015625 0.03125V4.64936L1.18013 1.08942L0.015625 0.03125Z'
            fill='#0F0F0F'
          />
          <Path
            d='M1.18013 1.09375L2.71054 1.09993L0.015625 4.65369L1.18013 1.09375Z'
            fill='black'
          />
          <Path
            d='M4.5166 1.09267L5.44874 0.046875H8.14988L5.87608 1.09886L4.5166 1.09267Z'
            fill='#646464'
          />
          <Path
            d='M4.51653 1.09375L5.87601 1.09993L1.43166 6.45623L0.0410156 6.473L4.51653 1.09375Z'
            fill='#4F4F4F'
          />
          <Path
            d='M0.0415023 6.47771L1.43215 6.46094L0.0263672 9.95282L0.0415023 6.47771Z'
            fill='#0F0F0F'
          />
          <Path
            d='M1.43215 6.45512L5.8765 1.09886L8.1503 0.046875L0.0263672 9.94695L1.43215 6.45512Z'
            fill='black'
          />
          <Path
            d='M1.86328 9.92716L4.19674 8.76475L8.67759 3.43854L10.0095 0.015625L1.86328 9.92716Z'
            fill='#4F4F4F'
          />
          <Path
            d='M4.19674 8.76562L5.54821 8.85229L4.5956 9.92371L1.86328 9.92811L4.19674 8.76562Z'
            fill='#0F0F0F'
          />
          <Path
            d='M10.0097 0.015625L8.67773 3.43853H10.0301L10.0097 0.015625Z'
            fill='#0F0F0F'
          />
          <Path
            d='M8.67812 3.4375H10.0305L5.54873 8.85034L4.19727 8.76375L8.67812 3.4375Z'
            fill='black'
          />
          <Path
            d='M7.36426 8.82082H8.83067L9.99246 5.32812L7.36426 8.82082Z'
            fill='#4F4F4F'
          />
          <Path
            d='M7.36495 8.8125H8.83123L10.0145 9.93874L6.52539 9.93427L7.36495 8.8125Z'
            fill='#0F0F0F'
          />
          <Path
            d='M9.99292 5.32812L10.0143 9.94713L8.83105 8.82082L9.99292 5.32812Z'
            fill='black'
          />
          <Path
            d='M18.333 10V8.22857L24.2592 1.24286L24.6044 2.24286H18.4625V0H27.1359V1.77143L21.2098 8.75714L20.8646 7.75714H27.3517V10H18.333ZM31.2168 3.87143H35.8915V5.98572H31.2168V3.87143ZM31.4181 7.81429H36.6826V10H28.5989V0H36.4956V2.18572H31.4181V7.81429ZM38.2631 10V0H43.4125C44.7358 0 45.7234 0.242857 46.3755 0.728573C47.0276 1.20476 47.3536 1.83334 47.3536 2.61429C47.3536 3.12857 47.2146 3.58095 46.9365 3.97143C46.668 4.35238 46.2844 4.65714 45.7858 4.88572C45.2968 5.10477 44.7118 5.21429 44.031 5.21429L44.3187 4.52857C45.0282 4.52857 45.6467 4.63809 46.1742 4.85714C46.7016 5.06667 47.1091 5.37619 47.3968 5.78572C47.694 6.18572 47.8427 6.67619 47.8427 7.25714C47.8427 8.12379 47.4878 8.8 46.7783 9.28572C46.0782 9.76193 45.0522 10 43.7001 10H38.2631ZM41.0823 7.95714H43.47C43.9591 7.95714 44.3282 7.87622 44.5776 7.71429C44.8365 7.54286 44.9659 7.28572 44.9659 6.94286C44.9659 6.6 44.8365 6.34762 44.5776 6.18572C44.3282 6.01429 43.9591 5.92857 43.47 5.92857H40.8809V3.97143H43.0385C43.5179 3.97143 43.8775 3.89048 44.1173 3.72857C44.357 3.56667 44.4769 3.32381 44.4769 3C44.4769 2.67619 44.357 2.43809 44.1173 2.28572C43.8775 2.12381 43.5179 2.04286 43.0385 2.04286H41.0823V7.95714ZM49.3178 10V0H53.9207C54.8412 0 55.6323 0.147619 56.294 0.442857C56.9652 0.738094 57.483 1.16667 57.8475 1.72857C58.2118 2.28095 58.394 2.93809 58.394 3.7C58.394 4.45238 58.2118 5.10477 57.8475 5.65714C57.483 6.2 56.9652 6.61905 56.294 6.91429C55.6323 7.2 54.8412 7.34286 53.9207 7.34286H50.9001L52.1658 6.15714V10H49.3178ZM55.546 10L53.0432 6.35714H56.0782L58.5954 10H55.546ZM52.1658 6.45714L50.9001 5.15714H53.7481C54.3425 5.15714 54.7837 5.02857 55.0714 4.77143C55.3686 4.51429 55.5173 4.15714 55.5173 3.7C55.5173 3.23334 55.3686 2.87143 55.0714 2.61429C54.7837 2.35714 54.3425 2.22857 53.7481 2.22857H50.9001L52.1658 0.928573V6.45714ZM58.757 10L63.2016 0H66.0065L70.4655 10H67.5024L64.0215 1.38572H65.1434L61.6626 10H58.757ZM61.1879 8.05714L61.9215 5.97143H66.8407L67.5743 8.05714H61.1879ZM72.0208 10V0H72.7544V9.34286H78.5511V10H72.0208ZM81.082 4.6H86.5334V5.24286H81.082V4.6ZM81.1683 9.34286H87.3821V10H80.4347V0H87.1663V0.657143H81.1683V9.34286ZM88.3739 10L93.0055 0H93.739L98.3706 10H97.5795L93.2068 0.457143H93.5233L89.165 10H88.3739ZM90.0712 7.18572L90.3157 6.57143H96.3425L96.587 7.18572H90.0712ZM100.199 10V0H103.809C104.653 0 105.377 0.133334 105.981 0.4C106.585 0.666666 107.05 1.05238 107.376 1.55714C107.702 2.05238 107.865 2.65714 107.865 3.37143C107.865 4.06667 107.702 4.66667 107.376 5.17143C107.05 5.66667 106.585 6.05238 105.981 6.32857C105.377 6.59524 104.653 6.72857 103.809 6.72857H100.602L100.932 6.38572V10H100.199ZM107.189 10L104.572 6.37143H105.406L108.024 10H107.189ZM100.932 6.42857L100.602 6.08572H103.809C104.892 6.08572 105.712 5.84762 106.269 5.37143C106.834 4.89524 107.117 4.22857 107.117 3.37143C107.117 2.50477 106.834 1.83809 106.269 1.37143C105.712 0.895237 104.892 0.657143 103.809 0.657143H100.602L100.932 0.300001V6.42857ZM110.551 10V0H111.169L118.347 9.1H118.016V0H118.75V10H118.146L110.954 0.900001H111.284V10H110.551Z'
            fill='black'
          />
        </Svg>
      </View>
      <Text style={styles.websiteUrl}>{websiteUrl}</Text>
    </View>
  );
  const renderBookInfo = () => (
    <View style={styles.bookInfoSection}>
      <View style={styles.bookInfoRow}>
        <View style={styles.bookInfoMeta}>
          <Text style={styles.bookTitle}>{title}</Text>
          <Text style={styles.bookAuthor}>By {author}</Text>
        </View>
        <Link src={landingPageUrl} style={styles.viewBookButton}>
          View Book
        </Link>
      </View>
    </View>
  );

  const renderFooter = () => (
    <View style={styles.footer} fixed>
      <Text style={styles.footerLogo}>{logoText}</Text>
      <Text style={styles.footerUrl}>{websiteUrl}</Text>
    </View>
  );

  // --- NOTE CARD RENDER ---
  const renderNoteCard = (note: Note, index: number) => (
    <View key={`${note.id}-${index}`} style={styles.noteCard} wrap={false}>
      <View style={styles.noteHeader}>
        <Text style={styles.chapterText}>{note.chapter}</Text>
        <Text style={styles.pageNumberText}>Page No. {note.pageNumber}</Text>
      </View>
      <Text style={styles.noteDate}>{note.date}</Text>
      {note.excerpt && (
        <View style={styles.noteExcerpt}>
          <Text>"{note.excerpt}"</Text>
        </View>
      )}
      <View style={styles.noteContentBox}>
        <Text style={styles.noteContent}>{note.content}</Text>
      </View>
    </View>
  );

  // --- PAGINATE NOTES WITHOUT SPLITTING CARDS ---
  const getContentHeight = (isFirstPage: boolean) => {
    const fixed = HEADER_HEIGHT + HEADER_MARG + FOOTER_HEIGHT + FOOTER_MARG;
    const dynamic = isFirstPage ? BOOKINFO_HEIGHT + NOTES_HEADER_HEIGHT : 0;
    return A4_HEIGHT - fixed - dynamic;
  };

  /**
   * Returns: Array of { notes: Note[]; isFirstPage: boolean }
   */
  function paginateNotes(
    notes: Note[],
  ): { notes: Note[]; isFirstPage: boolean }[] {
    const pages: { notes: Note[]; isFirstPage: boolean }[] = [];
    let i = 0;
    let isFirstPage = true;
    while (i < notes.length) {
      const contentHeight = getContentHeight(isFirstPage);
      const page: Note[] = [];
      let usedHeight = 0;
      while (i < notes.length) {
        const note = notes[i];
        const noteHeight = note ? estimateNoteHeight(note) : 0;
        if (noteHeight > contentHeight) {
          // note too big for page; force alone
          if (page.length === 0) {
            if (note) page.push(note);
            i++;
          }
          break;
        }
        if (usedHeight + noteHeight > contentHeight) {
          break;
        }
        if (note) page.push(note);
        usedHeight += noteHeight;
        i++;
      }
      if (page.length > 0) {
        pages.push({ notes: page, isFirstPage });
      }
      isFirstPage = false;
      if (page.length === 0) i++;
    }
    return pages;
  }

  const pages = paginateNotes(notes);

  // --- FINAL RENDER ---
  return (
    <Document>
      {pages.map((page, pageIndex) => (
        <Page key={pageIndex} size='A4' style={styles.page}>
          {renderHeader()}
          <View style={styles.contentArea}>
            {page.isFirstPage && (
              <>
                {renderBookInfo()}
                <Text style={styles.notesHeader}>Notes:</Text>
              </>
            )}
            <View style={styles.notesContainer}>
              {page.notes.map((note: Note, noteIndex) =>
                renderNoteCard(note, noteIndex),
              )}
            </View>
          </View>
          {renderFooter()}
          <Text style={styles.pageNumber}>{pageIndex + 1}</Text>
        </Page>
      ))}
    </Document>
  );
};

export default NotePdfDocument;
